import {useNavigate} from "react-router-dom";

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import {Button, DialogContent, LinearProgress, Typography} from "@mui/material";
import {GuestRecordingStoppedOverlay, UploadStatusIndicator} from "../NewStudio/RecordingStoppedOverlay";
import {LiveKitRoom, usePersistentUserChoices} from "@livekit/components-react";
import {
    NewGuestStudioVideoConference,
    NewGuestStudioVideoConferenceVideoRecording,
} from "../../components/LibrettoConference";
import {
    AdaptRecordingMode,
    LibrettoPlan,
    LibrettoRecordingMode, 
    LibrettoStudioNameStyle, 
    LibrettoStudioTheme,
    StandardVideoResolution,
    StudioNameStyleString
} from "../../utils/utils";
import {collection, doc, onSnapshot, query, where} from "firebase/firestore";
import {db} from "../../Firebase";
import {kRoomStateCollectionName, RoomName} from "../../utils/CollectionConstants";
import {RecordingStateDisplay} from "../NewStudio/NewRecordButton";
import {updateParticipantStatus, mapLiveKitConnectionQuality, RESOLUTION_OPTIONS} from "../NewStudio/ParticipantStatusService";
import Dialog from "@mui/material/Dialog";
import {UnauthenticatedUserIntercomComponent} from "../../components/IntercomComponent";
import {
    SimpleAudioButton,
    SimpleScreenShareButton,
    SimpleVideoButton,
    LeaveCallButton,
    RecordingModeReadOnlyButton,
} from "../NewStudio/ControlBarButtons";
import { ensurePlaceholderMicCamTracksPublished } from "../NewStudio/livekitPlaceholderTracks";
import { RoomEvent } from "livekit-client";
import V2StudioSidebar, {StudioSidebarItems} from "../NewStudio/V2StudioSidebar";
import StudioChatSidebar from "../NewStudio/StudioChatSidebar";
import ContextMenu from "../../librettoEditor/components/ContextMenu/ContextMenu";
import {NewLogoIconWithText} from "../NewHome/HomeIcons";
import { HasPendingLocalRecordingWork } from "../../localRecording/LocalRecordingStorage";
import {
    getPreferredAudioInputDeviceId,
    getPreferredAudioOutputDeviceId,
    getPreferredVideoInputDeviceId,
    setPreferredAudioInputDeviceId,
    setPreferredAudioOutputDeviceId,
    setPreferredVideoInputDeviceId,
} from "../../utils/mediaDevicePreferences";

const serverUrl = 'wss://librettobeta-htow58t4.livekit.cloud'

// Guest-specific People sidebar showing only their own connection status
const GuestPeopleSidebar = ({uploadProgress, uploadCompleted, recordingInProgress, recordingStopped = false, connectionQuality = 'good', guestName = 'You'}) => {
    const getConnectionColor = () => {
        if (connectionQuality === 'excellent' || connectionQuality === 'good') return '#22C55E';
        if (connectionQuality === 'fair') return '#F59E0B';
        if (connectionQuality === 'poor') return '#EF4444';
        return '#9CA3AF';
    };
    
    const getConnectionLabel = () => {
        if (connectionQuality === 'excellent') return 'Excellent';
        if (connectionQuality === 'good') return 'Good';
        if (connectionQuality === 'fair') return 'Fair';
        if (connectionQuality === 'poor') return 'Poor';
        return 'Connected';
    };

    return (
        <Box sx={{ 
            height: '100%', 
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '12px', 
            p: '12px',
        }}>
            {/* Header */}
            <Typography sx={{
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'Inter, sans-serif',
                color: '#1F2937',
            }}>
                Your Status
            </Typography>
            
            {/* Status Card */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '12px',
                backgroundColor: 'var(--app-bg-surface)',
                border: '1px solid var(--app-border-subtle)',
                overflow: 'hidden',
            }}>
                {/* Connection Section */}
                <Box sx={{ p: 2 }}>
                    <Typography sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        fontFamily: 'Inter, sans-serif',
                        color: 'var(--app-text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1,
                    }}>
                        Connection
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 12.55a11 11 0 0 1 14.08 0" stroke={getConnectionColor()} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M1.42 9a16 16 0 0 1 21.16 0" stroke={getConnectionColor()} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
                                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" stroke={getConnectionColor()} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <circle cx="12" cy="20" r="1" fill={getConnectionColor()}/>
                            </svg>
                            <Typography sx={{
                                fontSize: '14px',
                                fontFamily: 'Inter, sans-serif',
                                color: '#374151',
                            }}>
                                Internet connection is {getConnectionLabel().toLowerCase()}
                            </Typography>
                        </Box>
                        <Box sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: getConnectionColor(),
                        }} />
                    </Box>
                </Box>
                
                {/* Upload Progress - shown during recording and after stopped */}
                {(recordingInProgress || recordingStopped) && (
                    <Box sx={{ 
                        px: 2, 
                        py: 1.5, 
                        borderTop: '1px solid #F3F4F6',
                        backgroundColor: '#FAFAFA',
                    }}>
                        <Typography sx={{
                            fontSize: '12px',
                            fontWeight: 500,
                            fontFamily: 'Inter, sans-serif',
                            color: 'var(--app-text-muted)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            mb: 1,
                        }}>
                            Upload
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{
                                fontSize: '14px',
                                fontFamily: 'Inter, sans-serif',
                                color: '#374151',
                                fontWeight: 500,
                            }}>
                                {uploadCompleted ? 'Upload complete' : 'Uploading recording...'}
                            </Typography>
                            <Typography sx={{
                                fontSize: '14px',
                                fontFamily: 'Inter, sans-serif',
                                color: uploadCompleted ? '#22C55E' : '#6B7280',
                                fontWeight: 500,
                            }}>
                                {Math.round(uploadProgress)}%
                            </Typography>
                        </Box>
                        <Box sx={{
                            height: 6,
                            backgroundColor: '#E5E7EB',
                            borderRadius: 3,
                            overflow: 'hidden',
                        }}>
                            <Box sx={{
                                height: '100%',
                                width: `${uploadProgress}%`,
                                backgroundColor: uploadCompleted ? '#22C55E' : '#3B82F6',
                                borderRadius: 3,
                                transition: 'width 0.3s ease',
                            }} />
                        </Box>
                    </Box>
                )}
            </Box>
            
            {(recordingInProgress || recordingStopped) && (
                <Box sx={{
                    p: 1.5,
                    backgroundColor: recordingStopped ? '#FEF2F2' : '#FEF3C7',
                    borderRadius: '8px',
                    border: `1px solid ${recordingStopped ? '#FECACA' : '#FCD34D'}`,
                }}>
                    <Typography sx={{
                        fontSize: '12px',
                        fontFamily: 'Inter, sans-serif',
                        color: recordingStopped ? '#991B1B' : '#92400E',
                    }}>
                        {recordingStopped 
                            ? "Recording ended. Please keep this tab open until upload completes."
                            : "Recording in progress. Your audio/video is being captured locally. Don't close this tab."
                        }
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

		const NewGuestStudio = ({guestToken, userChoices, participantIdentifier, userFriendlyStudioName, roomName, hostIdentity}) => {
		    const navigate = useNavigate();
		    const theme = useTheme();
            const isDarkUi = theme.palette.mode === "dark";
		    const isTooSmall = useMediaQuery(theme.breakpoints.down('smd'));

    const studioThemeToBackgroundColor = (themeArg) => {
        if (themeArg === LibrettoStudioTheme.Libretto) return "#2B6BBD";
        if (themeArg === LibrettoStudioTheme.Dark || isDarkUi) return "var(--app-bg-canvas)";
        return "var(--app-bg-canvas)";
    }

    const pageStyle = (studioTheme) => ({
            paddingTop: "12px",
        height: "100%",
        width: "100%",
        background: `radial-gradient(circle at top, ${studioThemeToBackgroundColor(studioTheme)} 0%, ${studioThemeToBackgroundColor(studioTheme)} 100%)`,
    });

    const [backgroundImage, setBackgroundImage] = useState({isBlur: false, imageUrl: null});

    const isXl = useMediaQuery(theme.breakpoints.up('xl'));
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));

    const [videoAreaFullScreen, setVideoAreaFullScreen] = useState(false);

    const videoHeight = videoAreaFullScreen ? "100vh" : "calc(100vh - 240px)";
    const videoWidth = videoAreaFullScreen ? "100vw" : "calc((100vh - 240px) * 16 / 9)";

    const videoDisplayStyle = (videoAreaFullScreen) => ({
            height: videoHeight,
            width: videoWidth,
            maxWidth: videoAreaFullScreen ? "100vw" : "90vw",
    });

    const controlBarMarginBottom = "5px";

    const [showRecordingEndedCard, setShowRecordingEndedCard] = useState(true);

    // Keyboard handler for fullscreen toggle - properly in useEffect with cleanup
    useEffect(() => {
        const handleKeyDown = (event) => {
        if (event.ctrlKey && event.key === 'f') {
                event.preventDefault();
                setVideoAreaFullScreen(prev => !prev);
            } else if (event.key === 'Escape') {
            setVideoAreaFullScreen(false);
        }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const [cloudEgressStarted, setCloudEgressStarted] = useState(false);
    const [recordingInProgress, setRecordingInProgress] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const [recordingStarting, setRecordingStarting] = useState(false);
    const [localRecordingInProgress, setLocalRecordingInProgress] = useState(false);
    const [hasSeenRecordingStart, setHasSeenRecordingStart] = useState(false);
    const [uploadCompleted, setUploadCompleted] = useState(false);  // From Firestore allUploadsCompleted field
    const [noiseCancellationEnabled, setNoiseCancellationEnabled] = useState(false);
    const [localRecordingEnabled, setLocalRecordingEnabled] = useState(false);
    const [videoMirrored, setVideoMirrored] = useState(false);
    const [recordingMode, setRecordingMode] = useState(LibrettoRecordingMode.Audio);
    const [studioTheme, setStudioTheme] = useState(LibrettoStudioTheme.Light);
    const [studioNameStyle, setStudioNameStyle] = useState(LibrettoStudioNameStyle.Clean);
    const [recordingDisabled, setRecordingDisabled] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [screenShareUploadProgress, setScreenShareUploadProgress] = useState(null);
    const [myConnectionQuality, setMyConnectionQuality] = useState('good');
    const assetIdRef = useRef(null);
    const [localRecordingStopped, setLocalRecordingStopped] = useState(false);
    const [hasBackgroundUploads, setHasBackgroundUploads] = useState(false);
    const [selectedBrandLogo, setSelectedBrandLogo] = useState({assetId: null, url: null});
	    const [screenRecordingLocalTrackId, setScreenRecordingLocalTrackId] = useState(null);
	    const screenRecordingLocalTrackIdRef = useRef(null);
	    const [room, setRoom] = useState(null);
	    const hasSeenRecordingStartRef = useRef(false);
	    const firstRoomStateSnapshotRef = useRef(true);
	    const stableParticipantIdentifierRef = useRef(participantIdentifier);

	    // Guard against accidentally using LiveKit identity ("name") where we need the stable participantIdentifier.
	    useEffect(() => {
	        const next = typeof participantIdentifier === 'string' ? participantIdentifier.trim() : '';
	        if (!next) return;
	        const identity = (() => {
	            try {
	                return room?.localParticipant?.identity || '';
	            } catch {
	                return '';
	            }
	        })();
	        // If the next value equals the LiveKit identity (often a short "name"), don't overwrite a previously
	        // captured participantIdentifier.
	        if (identity && next === identity) return;
	        stableParticipantIdentifierRef.current = next;
	    }, [participantIdentifier, room]);

    const effectiveUploadProgress = useMemo(() => {
        const parts = [uploadProgress];
        if (typeof screenShareUploadProgress === 'number') parts.push(screenShareUploadProgress);
        const denom = parts.length;
        return denom > 0 ? parts.reduce((a, b) => a + b, 0) / denom : 0;
    }, [uploadProgress, screenShareUploadProgress]);

    // Keep a ref for guarding "recording stopped" UI against stale state.
    useEffect(() => {
        hasSeenRecordingStartRef.current = hasSeenRecordingStart;
    }, [hasSeenRecordingStart]);

    // Keep a ref of the screenRecordingLocalTrackId for logging inside Firestore snapshot closures.
    useEffect(() => {
        screenRecordingLocalTrackIdRef.current = screenRecordingLocalTrackId;
    }, [screenRecordingLocalTrackId]);

    // Reset per-room UI state when switching rooms (or re-joining without a full reload).
    useEffect(() => {
        firstRoomStateSnapshotRef.current = true;
        setHasSeenRecordingStart(false);
        setLocalRecordingStopped(false);
        setShowRecordingEndedCard(true);
        assetIdRef.current = null;
    }, [roomName]);

    // Thin indicator for background uploads from older recordings (Dexie-backed local uploader).
    useEffect(() => {
        let disposed = false;

        const check = async () => {
            try {
                const pending = await HasPendingLocalRecordingWork();
                if (disposed) return;
                setHasBackgroundUploads(prev => (prev === pending ? prev : pending));
            } catch {}
        };

        void check();
        const intervalId = setInterval(check, 3000);

        return () => {
            disposed = true;
            clearInterval(intervalId);
        };
    }, []);

    const setLocalRecordingStoppedForCurrentRecording = useCallback((stopped) => {
        if (!stopped) {
            setLocalRecordingStopped(false);
            return;
        }
        // Ignore "stopped" signals when this page hasn't seen a recording start.
        if (!hasSeenRecordingStartRef.current) return;
        setLocalRecordingStopped(true);
    }, []);

    const nowMs = () => {
        try {
            if (typeof performance !== 'undefined' && typeof performance.now === 'function') return performance.now();
        } catch {}
        return Date.now();
    };

    const recordingStartMsRef = useRef(null);
    const recordingStopMsRef = useRef(null);
    useEffect(() => {
        if (recordingInProgress) {
            recordingStartMsRef.current = nowMs();
            recordingStopMsRef.current = null;
            return;
        }
        if (recordingStartMsRef.current != null && recordingStopMsRef.current == null) {
            recordingStopMsRef.current = nowMs();
        }
    }, [recordingInProgress]);

    const getRecordingElapsedMs = () => {
        const startedAt = recordingStartMsRef.current;
        if (startedAt == null) return 0;
        const endedAt = recordingStopMsRef.current;
        const endTime = endedAt != null ? endedAt : nowMs();
        return Math.max(0, endTime - startedAt);
    };

    useEffect(() => {
        if (!room) return;
        let disposed = false;
        const ensure = () => {
            if (disposed) return;
            ensurePlaceholderMicCamTracksPublished(room, {
                expectMicrophoneEnabled: !!userChoices?.audioEnabled,
                expectCameraEnabled: !!userChoices?.videoEnabled,
            }).catch(() => {});
        };
        ensure();
        try {
            room.on?.(RoomEvent.Connected, ensure);
            room.on?.(RoomEvent.Reconnected, ensure);
        } catch {}
        return () => {
            disposed = true;
            try {
                room.off?.(RoomEvent.Connected, ensure);
                room.off?.(RoomEvent.Reconnected, ensure);
            } catch {}
        };
    }, [room]);
    
    // Refs to track current values for Firestore snapshot handlers (avoiding stale closures)
    const countdownRef = useRef(null);
    const cloudEgressStartedRef = useRef(false);
    const highQualityRecordingRef = useRef(null);
    const [sidebarItemSelected, setSidebarItemSelected] = useState("");
    const [selectedVideoResolution, setSelectedVideoResolution] = useState('1080p');

    const handleRecordingRestart = () => {
        setLocalRecordingStopped(false);
        setUploadCompleted(false);
        setUploadProgress(0);
        setScreenShareUploadProgress(null);
        setShowRecordingEndedCard(true);
    };

    const startHQRecording = () => {
        if (highQualityRecordingRef.current && !highQualityRecordingRef.current.isHighQualityRecordingActive()) {
            highQualityRecordingRef.current.startRecording();
        }
    };

    const reliablyStartRecording = () => {
        startHQRecording();
        setTimeout(() => startHQRecording(), 300);
        setTimeout(() => startHQRecording(), 600);
    };

    const stopHQRecording = () => {
        if (highQualityRecordingRef.current && highQualityRecordingRef.current.isHighQualityRecordingActive()) {
            highQualityRecordingRef.current.stopRecording();
        }
    };

    const reliablyStopRecording = () => {
        stopHQRecording();
        setTimeout(() => stopHQRecording(), 300);
        setTimeout(() => stopHQRecording(), 600);
    };

    // Keep refs in sync with state for use in Firestore snapshot handlers
    useEffect(() => {
        countdownRef.current = countdown;
    }, [countdown]);

    useEffect(() => {
        cloudEgressStartedRef.current = cloudEgressStarted;
    }, [cloudEgressStarted]);

    // Main room state listener - uses refs to avoid stale closures and functional updates to prevent unnecessary re-renders
    useEffect(() => {
        if (roomName !== '') {
            const q = query(collection(db, kRoomStateCollectionName), where(RoomName, "==", roomName));
            const unsubscribe = onSnapshot(q, (querySnapshot) => {
                const newRoomState = [];
                querySnapshot.forEach((doc) => {
                    newRoomState.push(doc.data());
                });
	                if (newRoomState.length > 0) {
	                    const data = newRoomState[0];
	                    const isFirstSnapshot = firstRoomStateSnapshotRef.current;
	                    firstRoomStateSnapshotRef.current = false;
	                    
	                    // Only update state if values have actually changed (prevents unnecessary re-renders)
	                    setLocalRecordingEnabled(prev => {
	                        const newVal = data.hasOwnProperty("localRecordingEnabled") ? data.localRecordingEnabled : true;
	                        return prev === newVal ? prev : newVal;
                    });
                    setVideoMirrored(prev => {
                        const newVal = data.hasOwnProperty("videoMirrored") ? data.videoMirrored : false;
                        return prev === newVal ? prev : newVal;
                    });
                    setRecordingMode(prev => {
                        const newVal = AdaptRecordingMode(data.recordingType);
                        return prev === newVal ? prev : newVal;
                    });
                    setStudioTheme(prev => {
                        const newVal = data.studioTheme ? data.studioTheme : LibrettoStudioTheme.Light;
                        return prev === newVal ? prev : newVal;
                    });
                    setStudioNameStyle(prev => {
                        const newVal = data.studioNameStyle ? data.studioNameStyle : LibrettoStudioNameStyle.Clean;
                        return prev === newVal ? prev : newVal;
                    });
                    setSelectedBrandLogo(prev => {
                        const newVal = data.studioBrandLogo && data.studioBrandLogo != "" ? data.studioBrandLogo : {assetId: null, url: null};
                        return JSON.stringify(prev) === JSON.stringify(newVal) ? prev : newVal;
                    });
	                    
                    // RoomNotRecording
                    if (data.roomState === 0) {
                        assetIdRef.current = null;
                        // If we join a room that is already idle, don't show the "recording ended" UI.
                        if (isFirstSnapshot) {
                            setHasSeenRecordingStart(false);
                            setLocalRecordingStopped(false);
                        }
	                        setRecordingInProgress(false);
	                        setCloudEgressStarted(false);
	                        setCountdown(null);
	                        setRecordingStarting(false);
	                        reliablyStopRecording();
	                    }
                    // RoomRecordingStarting - use ref to check current countdown value (avoids stale closure)
                    if (data.roomState === 1 && countdownRef.current === null) {
                        setHasSeenRecordingStart(true);
                        setRecordingStarting(true);
                        assetIdRef.current = data.currentAssetId;
                        try {
                            console.log('[NewGuestStudio] roomState=1 recording starting', {
                                assetId: assetIdRef.current,
                                screenRecordingLocalTrackId: screenRecordingLocalTrackIdRef.current,
                            });
                        } catch {}
                        setCountdown(3);
                        handleRecordingRestart();
                    }
                    // RoomRecordingInProgress - use ref to check current cloudEgressStarted value (avoids stale closure)
                    if (data.roomState === 2 && !cloudEgressStartedRef.current) {
                        // Guests may join mid-recording (roomState=2) and miss the roomState=1 transition
                        // where we normally set this. Screen-share session recording requires currentAssetId.
                        if (data.currentAssetId && assetIdRef.current !== data.currentAssetId) {
                            assetIdRef.current = data.currentAssetId;
                        }
                        try {
                            console.log('[NewGuestStudio] roomState=2 recording in progress', {
                                assetId: assetIdRef.current,
                                screenRecordingLocalTrackId: screenRecordingLocalTrackIdRef.current,
                            });
                        } catch {}
                        setHasSeenRecordingStart(true);
                        setCloudEgressStarted(true);
                        reliablyStartRecording();
                    }
	                }
	            }, (error) => {
                // Log Firestore errors - Firestore handles token refresh internally
                console.error('Firestore room state listener error:', error);
            });

            return () => unsubscribe();
        }
    }, [roomName]);

    // Subscribe to this participant's entry for noiseCancellationEnabled, allUploadsCompleted, and localTrackId
    useEffect(() => {
        if (roomName !== '' && participantIdentifier !== '') {
            const participantRef = doc(db, kRoomStateCollectionName, roomName, 'participants', participantIdentifier);
            const unsubscribe = onSnapshot(participantRef, (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const data = docSnapshot.data();
                    setNoiseCancellationEnabled(prev => {
                        const newVal = data.noiseCancellationEnabled ?? false;
                        return prev === newVal ? prev : newVal;
                    });
                    // Track upload completion from backend
                    setUploadCompleted(prev => {
                        const newVal = data.allUploadsCompleted === true;
                        return prev === newVal ? prev : newVal;
                    });
                    // Track localTrackId for deferred trackId delivery via Firestore
                    // This provides a reliable fallback when WebRTC metadata delivery fails
                    if (data.localTrackId && highQualityRecordingRef.current) {
                        highQualityRecordingRef.current.onTrackIdReceived?.(data.localTrackId);
                    }
                    setScreenRecordingLocalTrackId((prev) => {
                        const next = data.screenRecordingLocalTrackId || null;
                        if (prev !== next) {
                            try {
                                console.log('[NewGuestStudio] screenRecordingLocalTrackId updated', {
                                    assetId: assetIdRef.current,
                                    prev,
                                    next,
                                });
                            } catch {}
                        }
                        return prev === next ? prev : next;
                    });
                }
            }, (error) => {
                // Log Firestore errors - Firestore handles token refresh internally
                console.error('Firestore participant listener error:', error);
            });
            return () => unsubscribe();
        }
    }, [roomName, participantIdentifier]);

    // Send connection quality and upload progress updates periodically
	    useEffect(() => {
	        if (roomName === '' || guestToken === '' || !room) return;

	        const sendStatusUpdate = () => {
	            const localParticipant = room.localParticipant;
	            if (!localParticipant) return;

	            const stableParticipantIdentifier = stableParticipantIdentifierRef.current;
	            if (!stableParticipantIdentifier) return;
	            try {
	                if (stableParticipantIdentifier === localParticipant.identity) {
	                    console.warn('[NewGuestStudio] Skipping updateParticipantStatus: participantIdentifier matched LiveKit identity', {
	                        participantIdentifier: stableParticipantIdentifier,
	                        identity: localParticipant.identity,
	                    });
	                    return;
	                }
	            } catch {}

	            const quality = mapLiveKitConnectionQuality(localParticipant.connectionQuality);
	                updateParticipantStatus(guestToken, {
	                    roomName,
	                    participantIdentifier: stableParticipantIdentifier,
	                    connectionQuality: quality,
	                    videoResolution: selectedVideoResolution,
	                    // Send upload progress during recording (real-time uploads) and after stopped
	                    uploadProgress: (recordingInProgress || (localRecordingStopped && hasSeenRecordingStart)) ? effectiveUploadProgress : undefined,
	                }).catch((err) => console.error('Failed to update participant status:', err));
	        };

        // Send initial update
        sendStatusUpdate();

        // Send updates every 5 seconds (more frequent for upload progress)
        const intervalId = setInterval(sendStatusUpdate, 5000);

        return () => clearInterval(intervalId);
    }, [roomName, guestToken, room, selectedVideoResolution, effectiveUploadProgress, localRecordingStopped, hasSeenRecordingStart, recordingInProgress]);

    // Handler for resolution change - sends update immediately
	    const handleResolutionChange = (resolution) => {
	        setSelectedVideoResolution(resolution);
	        if (roomName && guestToken) {
	            const stableParticipantIdentifier = stableParticipantIdentifierRef.current;
	            if (!stableParticipantIdentifier) return;
	            updateParticipantStatus(guestToken, {
	                roomName,
	                participantIdentifier: stableParticipantIdentifier,
	                videoResolution: resolution,
	            }).catch(err => console.error('Failed to update video resolution:', err));
	        }
	    };

    useEffect(() => {
        if (cloudEgressStarted && countdown === null) {
            setRecordingInProgress(true);
        }
    }, [cloudEgressStarted, countdown]);

    useEffect(() => {
        setLocalRecordingInProgress(recordingStarting || recordingInProgress);
    }, [recordingStarting, recordingInProgress]);

    useEffect(() => {
        let intervalId;
        if (countdown > 0) {
            intervalId = setInterval(() => {
                setCountdown((prevCountdown) => prevCountdown - 1);
            }, 1000);
        } else if (countdown === 0) {
            setCountdown(null);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [countdown]);

    const countdownBoxStyle = {
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(255,255,255,0.88)",
        backdropFilter: "blur(4px)",
        zIndex: 1000,
    };

    const countdownTextStyle = {
        color: "var(--app-text-primary)",
        fontSize: "96px",
        fontWeight: 600,
        fontFamily: "Inter, sans-serif",
        lineHeight: 1,
    };
    
    const countdownLabelStyle = {
        color: "var(--app-text-muted)",
        fontSize: "12px",
        fontWeight: 500,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        marginTop: "16px",
    };

    const onLeave = () => {
        navigate("/dashboard");
    };

    const onDisconnected = (reason) => {
        // Log disconnection reason for debugging
        console.error("LiveKit room disconnected (guest)!", reason);
        console.log("Disconnection details:", {
            reason,
            wasRecording: recordingInProgress,
            roomName: roomName,
            timestamp: new Date().toISOString(),
        });
        // Don't navigate away automatically on disconnect - let the guest decide
        // The recording stopped overlay will show if recording was in progress
    };

    const soundBoardStyle = (videoAreaFullScreen) => ({
            height: "58px",
            marginTop: controlBarMarginBottom,
            paddingLeft: "16px",
            paddingRight: "16px",
            paddingTop: "10px",
            paddingBottom: "8px",
            display: videoAreaFullScreen ? 'none' : 'inline-flex',
            width: "auto",
            borderRadius: "0px",
            backgroundColor: "transparent",
    });

    const persistentUserChoices = usePersistentUserChoices({preventSave: false});

	    const {
	        saveAudioInputEnabled,
	        saveVideoInputEnabled,
	        saveAudioInputDeviceId,
	        saveVideoInputDeviceId,
	    } = persistentUserChoices;

    const saveAudioOutputDeviceId = persistentUserChoices?.saveAudioOutputDeviceId;

    const lobbyAudioInputDeviceId = userChoices?.audioDeviceId || '';
    const lobbyVideoInputDeviceId = userChoices?.videoDeviceId || '';
    const lobbyAudioOutputDeviceId = userChoices?.outputDeviceId || '';

    const preferredAudioInputDeviceId = useMemo(() => {
        if (lobbyAudioInputDeviceId && lobbyAudioInputDeviceId !== 'none') {
            return lobbyAudioInputDeviceId;
        }
        return getPreferredAudioInputDeviceId();
    }, [lobbyAudioInputDeviceId]);

    const preferredVideoInputDeviceId = useMemo(() => {
        if (lobbyVideoInputDeviceId && lobbyVideoInputDeviceId !== 'none') {
            return lobbyVideoInputDeviceId;
        }
        return getPreferredVideoInputDeviceId();
    }, [lobbyVideoInputDeviceId]);

    const preferredAudioOutputDeviceId = useMemo(() => {
        if (lobbyAudioOutputDeviceId) {
            return lobbyAudioOutputDeviceId;
        }
        return getPreferredAudioOutputDeviceId() || 'default';
    }, [lobbyAudioOutputDeviceId]);

    useEffect(() => {
        if (preferredAudioInputDeviceId) {
            setPreferredAudioInputDeviceId(preferredAudioInputDeviceId);
        }
    }, [preferredAudioInputDeviceId]);

    useEffect(() => {
        if (preferredVideoInputDeviceId) {
            setPreferredVideoInputDeviceId(preferredVideoInputDeviceId);
        }
    }, [preferredVideoInputDeviceId]);

    useEffect(() => {
        if (preferredAudioOutputDeviceId) {
            setPreferredAudioOutputDeviceId(preferredAudioOutputDeviceId);
        }
    }, [preferredAudioOutputDeviceId]);

    const handleSaveAudioInputDeviceId = useCallback((deviceId) => {
        saveAudioInputDeviceId(deviceId);
        setPreferredAudioInputDeviceId(deviceId);
    }, [saveAudioInputDeviceId]);

    const handleSaveVideoInputDeviceId = useCallback((deviceId) => {
        saveVideoInputDeviceId(deviceId);
        setPreferredVideoInputDeviceId(deviceId);
    }, [saveVideoInputDeviceId]);

    const handleSaveAudioOutputDeviceId = useCallback((deviceId) => {
        saveAudioOutputDeviceId?.(deviceId);
        setPreferredAudioOutputDeviceId(deviceId);
    }, [saveAudioOutputDeviceId]);

	    const showCurrentRecordingEndedUI = localRecordingStopped && hasSeenRecordingStart;
	    const showBackgroundUploadIndicator =
	        hasBackgroundUploads && !recordingInProgress && !recordingStarting && !showCurrentRecordingEndedUI;
	
	    return (
	        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--app-bg-canvas)',
                gap: '8px',
                height: '100vh',
                width: '100vw',
                overflow: 'hidden',
            }}
	        >
	            <ContextMenu/>

	            {showBackgroundUploadIndicator && (
	                <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000 }}>
	                    <LinearProgress sx={{ height: '2px' }} />
	                </Box>
	            )}
	            
	            {/* Top bar - just studio name and logo for guests */}
	            <Box sx={{
	                height: '40px',
                width: '100%',
                paddingY: "8px",
                paddingLeft: "24px",
                paddingRight: "10px",
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <Typography sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "var(--app-text-primary)",
                }}>
                    {userFriendlyStudioName}
                </Typography>
                <Box>
                    <NewLogoIconWithText/>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flex: 1,
                    gap: '8px',
                    minHeight: 0,
                    position: 'relative', // For absolute positioned sidebar
                }}
            >
                {/* Left area: main content */}
                <Box
                    sx={{
                        background: 'var(--app-bg-surface)',
                        display: 'flex',
                        marginLeft: "8px",
                        marginBottom: "3px",
                        flex: '1 1 0',
                        minWidth: 0,
                        border: "0.5px solid #E5E7EB",
                        borderRadius: "12px",
                        transition: 'all 0.3s ease',
                        position: 'relative',
                    }}
                >
            <UnauthenticatedUserIntercomComponent/>
            {/*<Dialog*/}
            {/*    open={isTooSmall}*/}
            {/*    onClose={() => {}}*/}
            {/*    aria-labelledby="form-dialog-title"*/}
            {/*>*/}
            {/*            <DialogContent sx={{*/}
            {/*                borderRadius: '30px',*/}
            {/*                display: 'flex',*/}
            {/*                justifyContent: 'center',*/}
            {/*                alignItems: 'center',*/}
            {/*                minWidth: {xs: '300px', md: '400px'},*/}
            {/*    }}>*/}
            {/*        <Typography><b>Please switch to a larger screen.</b> For now, you need a larger screen here.</Typography>*/}
            {/*    </DialogContent>*/}
            {/*</Dialog>*/}
                    
	                    <Grid container direction="column" style={pageStyle(studioTheme)} justifyContent="center" alignItems="center">
	                        {/* Recording stopped overlay */}
		                        {showCurrentRecordingEndedUI && showRecordingEndedCard && (
		                            <GuestRecordingStoppedOverlay 
		                                uploadProgress={effectiveUploadProgress}
		                                isComplete={uploadCompleted}
		                                onClose={() => setShowRecordingEndedCard(false)}
		                            />
		                        )}
	                        
	                        {/* Mini indicator to re-open overlay when closed */}
	                        {showCurrentRecordingEndedUI && !showRecordingEndedCard && (
	                            <UploadStatusIndicator
	                                isUploading={!uploadCompleted}
	                                isComplete={uploadCompleted}
	                                onClick={() => setShowRecordingEndedCard(true)}
	                            />
	                        )}
                        
            <Grid item sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                <Box style={videoDisplayStyle(videoAreaFullScreen)}>
                            <LiveKitRoom
                                serverUrl={serverUrl} 
                                token={guestToken}
                                data-lk-theme="default"
                                video={userChoices.videoEnabled} 
                                audio={userChoices.audioEnabled}
                                style={{height: '100%', width: '100%', borderRadius: '8px'}}
                                connect={true} 
                                onDisconnected={onDisconnected}
                            >
                                {recordingMode === LibrettoRecordingMode.Audio ? (
                                    <NewGuestStudioVideoConference
                        assetIdRef={assetIdRef}
                        localRecordingEnabled={localRecordingEnabled}
                        backgroundImage={backgroundImage}
                        recordingInProgress={recordingInProgress}
                        setUploadProgress={setUploadProgress}
                        highQualityRecordingRef={highQualityRecordingRef}
                        themeBackgroundColor={studioThemeToBackgroundColor(studioTheme)}
                        videoMirrored={videoMirrored}
                        startRecording={null}
                        roomName={roomName}
                        studioNameStyle={StudioNameStyleString(studioNameStyle)}
                        stopRecording={null}
                        hostIdentity={hostIdentity}
                        brandLogoUrl={selectedBrandLogo.url}
	                        enableKrispNoiseCancellation={noiseCancellationEnabled}
	                        localRecordingInProgress={localRecordingInProgress}
	                        livekitToken={guestToken}
	                        setRecordingDisabled={setRecordingDisabled}
	                        setLocalRecordingStopped={setLocalRecordingStoppedForCurrentRecording}
	                        videoResolution={selectedVideoResolution}
	                        setRoom={setRoom}
	                                    />
	                                ) : (
		                                    <NewGuestStudioVideoConferenceVideoRecording
		                        assetIdRef={assetIdRef}
		                        setRecordingDisabled={setRecordingDisabled}
		                        setUploadProgress={setUploadProgress}
		                        localRecordingEnabled={localRecordingEnabled}
	                        themeBackgroundColor={studioThemeToBackgroundColor(studioTheme)}
	                        backgroundImage={backgroundImage}
	                        recordingInProgress={recordingInProgress}
                        highQualityRecordingRef={highQualityRecordingRef}
                        startRecording={null}
                        videoMirrored={videoMirrored}
                        roomName={roomName}
                        studioNameStyle={StudioNameStyleString(studioNameStyle)}
                        stopRecording={null}
                        hostIdentity={hostIdentity}
                        brandLogoUrl={selectedBrandLogo.url}
	                        enableKrispNoiseCancellation={noiseCancellationEnabled}
	                        localRecordingInProgress={localRecordingInProgress}
	                        livekitToken={guestToken}
	                        setLocalRecordingStopped={setLocalRecordingStoppedForCurrentRecording}
	                        videoResolution={selectedVideoResolution}
	                                        setRoom={setRoom}
	                                    />
	                                )}
                </LiveKitRoom>
                </Box>
            </Grid>
                        
                        <Grid item alignSelf="center">
                            {countdown !== null && countdown > 0 && (
                                <Box style={countdownBoxStyle}>
                                    <Box 
                                        key={countdown}
                                        sx={{
                                            ...countdownTextStyle,
                                            animation: 'countdownPop 0.3s ease-out',
                                            '@keyframes countdownPop': {
                                                '0%': { opacity: 0, transform: 'scale(1.05)' },
                                                '100%': { opacity: 1, transform: 'scale(1)' },
                                            },
                                        }}
                                    >
                                        {countdown}
                                    </Box>
                                    <Box style={countdownLabelStyle}>Recording starts in…</Box>
                                </Box>
                            )}
                        </Grid>
                        
            <Grid container item justifyContent="center" alignItems="center"
                              display={isTooSmall ? "none" : "inline-flex"} mb={0} mt={0}>
                <Grid container item justifyContent="center" alignItems="center" display="flex"
                      sx={{
                          backgroundColor: 'var(--app-bg-surface)',
                          borderTop: '1px solid #E5E7EB',
                          borderRadius: '0 0 14px 14px',
                          pt: 1.5,
                          pb: 1.75,
                          px: 2.5,
                      }}>
                    <Grid item style={soundBoardStyle(videoAreaFullScreen)}>
                                    <Box display="flex" gap={1.75} alignItems="center">
                                        {/* Leave Button */}
                                        <LeaveCallButton onClick={onLeave} />

                                        {/* Audio Button (Mic + Speaker) */}
                                        <SimpleAudioButton
                                            room={room}
                                            isMicEnabled={true}
                                            selectDisabled={false}
                                            saveAudioInputDevice={handleSaveAudioInputDeviceId}
                                            saveAudioOutputDevice={handleSaveAudioOutputDeviceId}
                                            initialAudioInputDeviceId={preferredAudioInputDeviceId}
                                            initialAudioOutputDeviceId={preferredAudioOutputDeviceId}
                                        />

                                        {/* Video Button (Camera + Resolution) */}
                                        <SimpleVideoButton
                                            room={room}
                                            isCamEnabled={true}
                                            selectDisabled={false}
                                            saveVideoInputDevice={handleSaveVideoInputDeviceId}
                                            initialVideoInputDeviceId={preferredVideoInputDeviceId}
                                            selectedResolution={selectedVideoResolution}
                                            onResolutionChange={handleResolutionChange}
                                        />

                                        <SimpleScreenShareButton room={room} />

                                        <RecordingModeReadOnlyButton
                                            recordingMode={recordingMode === LibrettoRecordingMode.Audio ? 'audio' : 'video'}
                                        />

                                        {/* Recording State Display (no record button for guests) */}
                                        <Box sx={{ ml: 1.25 }}>
                                            <RecordingStateDisplay
                                                recordingInProgress={recordingInProgress}
                                                localRecordingInProgress={localRecordingInProgress}
                                            />
                                        </Box>
                                    </Box>
                            </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>

                {/* Sidebars - overlay positioned */}
                {sidebarItemSelected && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        right: 100, // Account for the sidebar rail width
                        bottom: 0,
                        width: '300px',
                        zIndex: 10,
                        transition: 'all 0.2s ease',
                        background: 'var(--app-bg-surface)',
                        border: "0.5px solid #E5E7EB",
                        borderRadius: "12px",
                        overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    }}>
		                        {sidebarItemSelected === StudioSidebarItems.PEOPLE && (
		                            <GuestPeopleSidebar 
		                                uploadProgress={effectiveUploadProgress}
		                                uploadCompleted={uploadCompleted}
		                                recordingInProgress={recordingInProgress}
		                                recordingStopped={showCurrentRecordingEndedUI}
		                            />
		                        )}
                        {sidebarItemSelected === StudioSidebarItems.CHAT && (
                            <StudioChatSidebar room={room} />
                        )}
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        width: '92px',
                        flexShrink: 0,
                        flexDirection: 'column',
                        padding: '10px 8px 12px',
                        gap: '10px',
                        borderRadius: '14px',
                        border: '1px solid var(--app-border-subtle)',
                        backgroundColor: '#FBFCFD',
                    }}
                >
                    <Typography
                        sx={{
                            color: 'var(--app-text-muted)',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '10px',
                            fontWeight: 600,
                            lineHeight: '12px',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            width: '100%',
                            textAlign: 'center',
                            userSelect: 'none',
                        }}
                    >
                        Session
                    </Typography>
                    <V2StudioSidebar 
                        setSidebarItemSelected={setSidebarItemSelected}
                        sidebarItemSelected={sidebarItemSelected}
                    />
                </Box>
            </Box>
                    </Box>
    );
};

export default NewGuestStudio;
